import React, { useState, useEffect } from 'react';
import { MentorshipSidebar } from './MentorshipSidebar';
import { MentorshipTopbar } from './MentorshipTopbar';
import { MentorshipHubPage } from './MentorshipHubPage';
import { MentorshipCoursePage } from './MentorshipCoursePage';
import { MentorshipModulePage } from './MentorshipModulePage';
import { MentorshipLessonPage } from './MentorshipLessonPage';
import { MentorshipEventsPage } from './MentorshipEventsPage';
import { MentorshipSupportPage } from './MentorshipSupportPage';
import { MentorAdminModal } from './MentorAdminModal';
import { MentorEditorPage } from './MentorEditorPage';
import { MentorshipDashboardPage } from './MentorshipDashboardPage';
import { getMentorshipBySlug, MOCK_MENTORSHIPS } from '../../data/mockMentorshipData';
import { MentorAreaConfig, MentorModule, MentorLesson } from '../../types/mentorship';
import { useAuth } from '../../contexts/AuthContext';

import { mentorshipService } from '../../services/mentorshipService';

interface MentorshipContainerProps {
  onBackToMainPlatform: () => void;
  onOpenAuth?: () => void;
}

export const MentorshipContainer: React.FC<MentorshipContainerProps> = ({
  onBackToMainPlatform,
  onOpenAuth,
}) => {
  const { user, isMentor, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'inicio' | 'eventos' | 'suporte' | 'dashboard'>('inicio');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);

  const getSlugFromUrl = () => {
    const path = window.location.pathname;
    if (path.startsWith('/mentoria-')) {
      return path.replace('/mentoria-', '');
    }
    return 'vitor-terra'; // fallback default
  };

  const [currentSlug, setCurrentSlug] = useState<string>(getSlugFromUrl());
  const [mentorshipConfig, setMentorshipConfig] = useState<MentorAreaConfig>(() =>
    getMentorshipBySlug(currentSlug)
  );

  const [viewState, setViewState] = useState<'hub' | 'course' | 'module' | 'lesson' | 'editor'>(() => {
    if (window.location.pathname === '/mentoria-hub') return 'hub';
    return 'course';
  });

  const [activeModule, setActiveModule] = useState<MentorModule | null>(
    mentorshipConfig.modules[0] || null
  );
  const [activeLesson, setActiveLesson] = useState<MentorLesson | null>(
    mentorshipConfig.modules[0]?.lessons[0] || null
  );

  // Carregar dados reais do Supabase na inicialização e troca de slug
  useEffect(() => {
    let isMounted = true;
    const loadConfigFromSupabase = async () => {
      const slug = getSlugFromUrl();
      setCurrentSlug(slug);
      const remoteConfig = await mentorshipService.fetchMentorshipConfig(slug);
      if (isMounted) {
        setMentorshipConfig(remoteConfig);
        setActiveModule(remoteConfig.modules[0] || null);
        setActiveLesson(remoteConfig.modules[0]?.lessons[0] || null);
      }
    };

    loadConfigFromSupabase();

    const handlePopState = () => {
      loadConfigFromSupabase();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      isMounted = false;
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const navigateToMentorship = async (slug: string) => {
    const targetPath = `/mentoria-${slug}`;
    window.history.pushState(null, '', targetPath);
    setCurrentSlug(slug);

    const cfg = await mentorshipService.fetchMentorshipConfig(slug);
    setMentorshipConfig(cfg);
    setActiveModule(cfg.modules[0] || null);
    setActiveLesson(cfg.modules[0]?.lessons[0] || null);
    setViewState('course');
  };

  const handleUpdateConfig = async (newConfig: MentorAreaConfig) => {
    setMentorshipConfig(newConfig);

    if (newConfig.slug && newConfig.slug !== currentSlug) {
      const targetPath = `/mentoria-${newConfig.slug}`;
      window.history.pushState(null, '', targetPath);
      setCurrentSlug(newConfig.slug);
    }

    // Persistir no Supabase
    await mentorshipService.saveMentorshipConfigToSupabase(currentSlug, newConfig);
  };

  const handleUpdateYoutubeUrl = (newUrl: string) => {
    setMentorshipConfig((prev) => ({
      ...prev,
      heroSpoiler: {
        ...prev.heroSpoiler,
        youtubeUrl: newUrl,
      },
    }));
  };

  const handleToggleCompleteLesson = (lessonId: string) => {
    setMentorshipConfig((prev) => ({
      ...prev,
      modules: prev.modules.map((mod) => ({
        ...mod,
        lessons: mod.lessons.map((les) =>
          les.id === lessonId ? { ...les, isCompleted: !les.isCompleted } : les
        ),
      })),
    }));

    if (activeLesson && activeLesson.id === lessonId) {
      setActiveLesson((prev) => (prev ? { ...prev, isCompleted: !prev.isCompleted } : null));
    }
  };

  const handleSelectModule = (module: MentorModule) => {
    setActiveModule(module);
    setViewState('module');
  };

  const handleSelectLesson = (module: MentorModule, lesson: MentorLesson) => {
    setActiveModule(module);
    setActiveLesson(lesson);
    setViewState('lesson');
  };

  const canAccessDashboard = Boolean(
    isAdmin || (isMentor && (!mentorshipConfig.mentorUserId || user?.id === mentorshipConfig.mentorUserId))
  );

  const mentorshipList = Object.values(MOCK_MENTORSHIPS);

  const getBreadcrumbs = () => {
    if (activeTab === 'dashboard') return [{ label: 'Dashboard da Mentoria' }];
    if (activeTab === 'eventos') return [{ label: 'Eventos' }];
    if (activeTab === 'suporte') return [{ label: 'Suporte' }];

    if (viewState === 'hub') {
      return [{ label: 'Meus Cursos' }];
    }
    if (viewState === 'course') {
      return [{ label: mentorshipConfig.courseTitle }];
    }
    if (viewState === 'module' && activeModule) {
      return [
        { label: mentorshipConfig.courseTitle, onClick: () => setViewState('course') },
        { label: activeModule.title },
      ];
    }
    if (viewState === 'lesson' && activeModule && activeLesson) {
      return [
        { label: mentorshipConfig.courseTitle, onClick: () => setViewState('course') },
        { label: activeModule.title, onClick: () => setViewState('module') },
        { label: activeLesson.title },
      ];
    }
    return [];
  };

  if (viewState === 'editor') {
    return (
      <MentorEditorPage
        config={mentorshipConfig}
        onSaveConfig={handleUpdateConfig}
        onBack={() => setViewState('course')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#121316] text-gray-100 flex font-sans antialiased selection:bg-[#0FFF55]/30 selection:text-white">
      {/* 1. Lateral Left Thin Sidebar (Hubla Style) */}
      <MentorshipSidebar
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (tab === 'inicio') setViewState('course');
        }}
        onBackToMainPlatform={onBackToMainPlatform}
        accentColor={mentorshipConfig.branding.accentColor || '#0FFF55'}
        isMentorOrAdmin={canAccessDashboard}
      />

      {/* 2. Main Content Wrapper */}
      <div className="flex-1 flex flex-col md:pl-[70px] xl:pl-[76px] transition-all duration-300 w-full overflow-x-hidden min-w-0">
        {/* Topbar */}
        <MentorshipTopbar
          config={mentorshipConfig}
          breadcrumbs={getBreadcrumbs()}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onOpenAffiliationModal={() =>
            alert(`Solicitação de afiliação enviada para o mentor ${mentorshipConfig.mentorName}!`)
          }
          onOpenAdminPanel={() => setViewState('editor')}
          onOpenAuth={onOpenAuth}
        />

        {/* Dynamic Views */}
        <main className="flex-1 px-4 md:px-10 py-6 w-full">
          {activeTab === 'dashboard' && (
            <MentorshipDashboardPage
              config={mentorshipConfig}
              onOpenAdminPanel={() => setIsAdminModalOpen(true)}
              onUpdateConfig={handleUpdateConfig}
            />
          )}

          {activeTab === 'eventos' && (
            <MentorshipEventsPage
              config={mentorshipConfig}
              isMentorOrAdmin={isMentor || isAdmin}
              onOpenAdminPanel={() => setViewState('editor')}
            />
          )}

          {activeTab === 'suporte' && (
            <MentorshipSupportPage
              config={mentorshipConfig}
              isMentorOrAdmin={isMentor || isAdmin}
              onOpenAdminPanel={() => setViewState('editor')}
            />
          )}

          {activeTab === 'inicio' && (
            <>
              {viewState === 'hub' && (
                <MentorshipHubPage
                  mentorships={mentorshipList}
                  onSelectMentorship={navigateToMentorship}
                />
              )}

              {viewState === 'course' && (
                <MentorshipCoursePage
                  config={mentorshipConfig}
                  onUpdateYoutubeUrl={handleUpdateYoutubeUrl}
                  onSelectModule={handleSelectModule}
                  onSelectLesson={handleSelectLesson}
                />
              )}

              {viewState === 'module' && activeModule && (
                <MentorshipModulePage
                  config={mentorshipConfig}
                  currentModule={activeModule}
                  onSelectModule={setActiveModule}
                  onSelectLesson={handleSelectLesson}
                  onBackToCourse={() => setViewState('course')}
                />
              )}

              {viewState === 'lesson' && activeModule && activeLesson && (
                <MentorshipLessonPage
                  config={mentorshipConfig}
                  currentModule={activeModule}
                  currentLesson={activeLesson}
                  onSelectLesson={handleSelectLesson}
                  onToggleCompleteLesson={handleToggleCompleteLesson}
                  onBackToCourse={() => setViewState('module')}
                />
              )}
            </>
          )}
        </main>
      </div>

      {/* Modal do Painel de Administração do Mentor (Fallback) */}
      <MentorAdminModal
        isOpen={isAdminModalOpen}
        onClose={() => setIsAdminModalOpen(false)}
        config={mentorshipConfig}
        onSaveConfig={handleUpdateConfig}
      />
    </div>
  );
};
